import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import DropDown from "../../../Components/Shared/Dropdown/Dropdown";
import FormInputs from "../../../Components/Shared/FormInputs/FormInputs";
import FormDateInput from "../../../Components/Shared/FormDateInput/FormDateInput";
import { formatDisplayDate, formatDisplayDateTime } from "../../../Components/Shared/FormDateInput/dateFormat";
import EndPoints from "../../../Api/EndPoints";
import PencilIcon from "../../../Assets/Images/CommanImages/GPenz.png";
import YellowPlus from "../../../Assets/Images/CommanImages/YellowPlus.png";
import "./paymentrecord.css"

const PAYMENT_MODE_OPTIONS = [
  { id: 1, label: "Cash" },
  { id: 2, label: "Cheque" },
  { id: 3, label: "NEFT/IMPS" },
  { id: 4, label: "UPI" },
  { id: 5, label: "Refund Adjustment" },
  { id: 6, label: "Demand Draft" },
  { id: 7, label: "Credit Card" },
  { id: 8, label: "ECS" },
  { id: 9, label: "NACH" },
  { id: 10, label: "Online Gateway" },
  { id: 11, label: "Bank Deposit" },
  { id: 12, label: "Fee Waiver" },
];

// Payment modes with no physical bank account involved - Bank Name
// doesn't apply for these, so the field is disabled (and cleared).
const BANK_NAME_DISABLED_MODES = [1, 4, 12]; // Cash, UPI, Fee Waiver

// Cash and Fee Waiver have no transaction/reference number either.
const TRANSACTION_NO_DISABLED_MODES = [1, 12]; // Cash, Fee Waiver

const todayStr = () => new Date().toISOString().split("T")[0];

// Today's date in the user's LOCAL timezone as YYYY-MM-DD. todayStr()
// above goes through toISOString(), i.e. UTC - for IST that's still
// "yesterday" between midnight and 5:30am, which would wrongly block a
// cheque dated today. Used for the cheque-date vs payment-date check.
const localTodayStr = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const nextDayStr = (dateStr) => {
  const d = new Date(dateStr);
  d.setDate(d.getDate() + 1);
  return d.toISOString().split("T")[0];
};

const PDC_STATUS_OPTIONS = [
  { id: 1, label: "Pending" },
  { id: 2, label: "Realized" },
  { id: 3, label: "Canceled" },
  { id: 4, label: "Rejected" },
];

// Realized only ever happens as a side effect of actually paying that
// installment/cheque through Payment Detail - never manually selectable
// here, and once realized a cheque is locked (read-only) rather than
// offering any further transitions. From any other status:
// Pending -> Canceled or Rejected; Canceled -> Pending only;
// Rejected -> Pending or Canceled.
const getPdcStatusOptions = (currentStatus) => {
  if (currentStatus === 1) {
    return PDC_STATUS_OPTIONS.filter((s) => [1, 3, 4].includes(s.id));
  }
  if (currentStatus === 3) {
    return PDC_STATUS_OPTIONS.filter((s) => [3, 1].includes(s.id));
  }
  if (currentStatus === 4) {
    return PDC_STATUS_OPTIONS.filter((s) => [4, 1, 3].includes(s.id));
  }
  return PDC_STATUS_OPTIONS.filter((s) => s.id === currentStatus);
};

const formatINR = (value) => Number(value || 0).toLocaleString("en-IN");

function PaymentRecord({
  setMessageBox,
  setIsMessageBoxVisible,
  setActiveImg,
  setNoTimeSet,
  dataClient,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const studentId = searchParams.get("studentId");
  const userName = dataClient?.client_schema_name?.[6]?.[0]?.user_name || "";
  const cameFrom = searchParams.get("from");

  const [isOpenStudent, setIsOpenStudent] = useState(true);
  const [isOpenInstallments, setIsOpenInstallments] = useState(true);
  const [isOpenPayment, setIsOpenPayment] = useState(true);
  const [isOpenHistory, setIsOpenHistory] = useState(true);
  // Tracks how many of the initial page-load fetches (profile, fee plan,
  // course fees, payment history, bank list, PDCs) are still in flight -
  // a plain counter rather than one flag, since these all run in
  // parallel, independent useEffects.
  const [pendingInitialLoads, setPendingInitialLoads] = useState(0);
  const [studentProfile, setStudentProfile] = useState(null);
  const [feePlan, setFeePlan] = useState(null);
  const [sessionEndDate, setSessionEndDate] = useState(null);
  const [courseFeeRates, setCourseFeeRates] = useState({ sgst_prcnt: 0, cgst_prcnt: 0 });
  const [accStudentId, setAccStudentId] = useState("");
  const [installments, setInstallments] = useState([]);
  const [selectedInstallmentIds, setSelectedInstallmentIds] = useState([]);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [editableHistoryGroups, setEditableHistoryGroups] = useState([]);
  const [pristineHistoryGroups, setPristineHistoryGroups] = useState(null);
  const [isEditingHistory, setIsEditingHistory] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isOpenPdc, setIsOpenPdc] = useState(true);
  const [bankList, setBankList] = useState([]);
  const [existingCheques, setExistingCheques] = useState([]);
  const [isExistingChequesLoaded, setIsExistingChequesLoaded] = useState(false);
  const [cheques, setCheques] = useState([]);
  const [pristineCheques, setPristineCheques] = useState(null);
  const [pendingDeletePdcIds, setPendingDeletePdcIds] = useState([]);
  const hasSeededChequesRef = useRef(false);

  // Post dated cheque rows start out mirroring the fee plan's
  // installments 1:1 - one row per installment, installment_no
  // prefilled. If an installment already has one or more cheques on
  // file, those are shown directly in its row(s) (read-only, isExisting
  // true) instead of a blank editable row - an installment with
  // multiple cheques on file gets one row per cheque. Waits for both
  // installments and existingCheques to finish loading (they fetch
  // independently and can resolve in either order) and only seeds once
  // - installment_no stays editable from there, and more rows can be
  // added, so this must not re-run and clobber in-progress edits.
  useEffect(() => {
    if (installments.length === 0 || !isExistingChequesLoaded) return;
    if (hasSeededChequesRef.current) return;
    hasSeededChequesRef.current = true;

    const rows = [];
    if (existingCheques.length === 0) {
      installments.forEach((inst) => {
        rows.push({
          key: `pdc_${inst.fee_schedule_id}_${Date.now()}`,
          isExisting: false,
          selected: false,
          installment_no: inst.installment_no,
          amount:
            (Number(inst.amount) || 0) + (Number(inst.sgst) || 0) + (Number(inst.cgst) || 0) || "",
          cheque_no: "",
          cheque_date: "",
          bank_id: null,
          branch_name: "",
          status: 1,
        });
      });
    } else {
      existingCheques.forEach((c) => {
        rows.push({
          key: `pdc_existing_${c.pdc_id}`,
          isExisting: true,
          pdc_id: c.pdc_id,
          selected: false,
          installment_no: c.installment_no,
          amount: c.amount,
          cheque_no: c.cheque_no || "",
          cheque_date: c.cheque_date || "",
          bank_id: c.bank_id,
          bank_name: c.bank_name,
          branch_name: c.branch_name || "",
          status: c.status,
        });
      });
    }
    setCheques(rows);
    setPristineCheques(JSON.parse(JSON.stringify(rows)));
  }, [installments, isExistingChequesLoaded, existingCheques]);

  // Selecting an installment auto-selects every post-dated cheque row
  // whose installment_no matches it - matched by that value directly
  // now that installment_no is user-editable per row (not by row
  // position), since more than one cheque can point at the same
  // installment.
  const selectedInstallmentNumbers = useMemo(
    () =>
      new Set(
        installments
          .filter((inst) => selectedInstallmentIds.includes(inst.fee_schedule_id))
          .map((inst) => inst.installment_no)
      ),
    [installments, selectedInstallmentIds]
  );

  useEffect(() => {
    setCheques((prev) =>
      prev.map((c) =>
        c.status === 2
          ? c
          : { ...c, selected: selectedInstallmentNumbers.has(Number(c.installment_no)) }
      )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedInstallmentNumbers]);

  useEffect(() => {
    if (!studentId) return;
    setPendingInitialLoads((c) => c + 1);
    fetch(`${EndPoints.GetStudentProfileDetails}?student_id=${studentId}`, {
      headers: { accept: "application/json" },
    })
      .then((r) => r.json())
      .then((data) => {
        const profile = data?.data?.[0];
        if (data?.status === 200 && profile) {
          setStudentProfile(profile);
        }
      })
      .catch((err) => console.error("Error fetching student profile:", err))
      .finally(() => setPendingInitialLoads((c) => c - 1));
  }, [studentId]);

  // Same API EditFeePlan.jsx uses for the installment schedule - reused
  // here to seed Account Student ID (Student Detail Information, entered
  // manually by accounts - editable below, not wired to save yet) and
  // to drive the Fee Plan Installments section.
  useEffect(() => {
    if (!studentId) return;
    setPendingInitialLoads((c) => c + 1);
    const token = localStorage.getItem("other");
    fetch(`${EndPoints.GET_STUDENT_FEE_PLAN}?student_id=${studentId}`, {
      headers: { accept: "application/json", Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((data) => {
        const plans = data?.data || [];
        if (!plans.length) return;

        // Same "latest plan" convention the fee dashboards already use -
        // highest fee_plan_id wins when a student has more than one.
        const latestPlan = plans.reduce((latest, plan) =>
          !latest || plan.fee_plan_id > latest.fee_plan_id ? plan : latest
        , null);

        setFeePlan(latestPlan);
        if (latestPlan.session_id) {
          fetch(EndPoints.GET_SESSIONS_LIST, { headers: { accept: "application/json" } })
            .then((r) => r.json())
            .then((sessionsData) => {
              const sessions = Array.isArray(sessionsData?.data) ? sessionsData.data : [];
              const match = sessions.find(
                (s) => Number(s.session_id) === Number(latestPlan.session_id)
              );
              if (match?.session_end_date) {
                setSessionEndDate(match.session_end_date.split(" ")[0]);
              }
            })
            .catch((err) => console.error("Error fetching session end date:", err));
        }
        const existingValue = latestPlan.acc_student_id;
        const hasValue =
          existingValue !== null &&
          existingValue !== undefined &&
          existingValue !== "";
        setAccStudentId(hasValue ? existingValue : "");
        setInstallments(
          (latestPlan.fee_schedule_list || [])
            .slice()
            .sort((a, b) => a.installment_no - b.installment_no)
        );
      })
      .catch((err) => console.error("Error fetching fee plan:", err))
      .finally(() => setPendingInitialLoads((c) => c - 1));
  }, [studentId]);

  useEffect(() => {
    if (!studentId) return;
    setPendingInitialLoads((c) => c + 1);
    const token = localStorage.getItem("other");
    fetch(`${EndPoints.GET_COURSE_FEES_FOR_STUDENT}?student_id=${studentId}`, {
      headers: { accept: "application/json", Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((data) => {
        if (data?.status === 200 && data?.data) {
          setCourseFeeRates({
            sgst_prcnt: Number(data.data.sgst_prcnt) || 0,
            cgst_prcnt: Number(data.data.cgst_prcnt) || 0,
          });
        }
      })
      .catch((err) => console.error("Error fetching course fee rates:", err))
      .finally(() => setPendingInitialLoads((c) => c - 1));
  }, [studentId]);

  useEffect(() => {
    if (!studentId) return;
    setPendingInitialLoads((c) => c + 1);
    const token = localStorage.getItem("other");
    fetch(`${EndPoints.GET_STUDENT_FEE_PAYMENT_HISTORY}?student_id=${studentId}`, {
      headers: { accept: "application/json", Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((data) => {
        if (data?.status === 200) {
          setPaymentHistory(data?.data || []);
        }
      })
      .catch((err) => console.error("Error fetching payment history:", err))
      .finally(() => setPendingInitialLoads((c) => c - 1));
  }, [studentId]);

  useEffect(() => {
    setPendingInitialLoads((c) => c + 1);
    const token = localStorage.getItem("other");
    fetch(EndPoints.GET_BANK_LIST, {
      headers: { accept: "application/json", Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((data) => {
        if (data?.status === 200) {
          setBankList(data?.data || []);
        }
      })
      .catch((err) => console.error("Error fetching bank list:", err))
      .finally(() => setPendingInitialLoads((c) => c - 1));
  }, []);

  useEffect(() => {
    if (!studentId) return;
    setPendingInitialLoads((c) => c + 1);
    const token = localStorage.getItem("other");
    fetch(`${EndPoints.GET_POST_DATED_CHEQUES}?student_id=${studentId}`, {
      headers: { accept: "application/json", Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((data) => {
        if (data?.status === 200) {
          setExistingCheques(data?.data || []);
        }
        setIsExistingChequesLoaded(true);
      })
      .catch((err) => {
        console.error("Error fetching post dated cheques:", err);
        setIsExistingChequesLoaded(true);
      })
      .finally(() => setPendingInitialLoads((c) => c - 1));
  }, [studentId]);

  // Same shared message box used for errors/warnings/save messages -
  // shown while any of the initial page-load fetches above are still
  // in flight, hidden once they've all settled.
  useEffect(() => {
    if (pendingInitialLoads > 0) {
      setNoTimeSet && setNoTimeSet(true);
      showMessage("Loading data, please wait...");
    } else {
      setIsMessageBoxVisible(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingInitialLoads]);

  // Installment a newly added cheque row should default to: the first
  // installment (in order) that isn't fully paid yet and has no active
  // cheque on it - Canceled/Rejected cheques don't count as covering it.
  // If every open installment already has a cheque, falls back to the
  // installment after the last row's, capped at the final installment
  // (an extra cheque against the last one).
  const getNextChequeInstallment = () => {
    if (installments.length === 0) return null;
    const coveredNos = new Set(
      cheques
        .filter((c) => c.status !== 3 && c.status !== 4 && c.installment_no !== "")
        .map((c) => Number(c.installment_no))
    );
    const firstUncovered = installments.find((inst) => {
      const gross = (Number(inst.amount) || 0) + (Number(inst.sgst) || 0) + (Number(inst.cgst) || 0);
      const isFullyPaid = gross > 0 && (Number(inst.received) || 0) >= gross;
      return !isFullyPaid && !coveredNos.has(Number(inst.installment_no));
    });
    if (firstUncovered) return firstUncovered;
    const lastNo = Number(cheques[cheques.length - 1]?.installment_no) || 0;
    return (
      installments.find((inst) => Number(inst.installment_no) > lastNo) ||
      installments[installments.length - 1]
    );
  };

  const handleAddCheque = () => {
    const defaultInstallment = getNextChequeInstallment();
    setCheques((prev) => [
      ...prev,
      {
        key: `pdc_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
        isExisting: false,
        selected: defaultInstallment
          ? selectedInstallmentNumbers.has(Number(defaultInstallment.installment_no))
          : false,
        installment_no: defaultInstallment?.installment_no ?? "",
        amount: defaultInstallment
          ? (Number(defaultInstallment.amount) || 0) +
              (Number(defaultInstallment.sgst) || 0) +
              (Number(defaultInstallment.cgst) || 0) || ""
          : "",
        cheque_no: "",
        cheque_date: "",
        bank_id: null,
        branch_name: "",
        status: 1,
      },
    ]);
  };

  const handleRemoveCheque = (key) => {
    const target = cheques.find((c) => c.key === key);
    if (target?.pdc_id) {
      setPendingDeletePdcIds((prev) => [...prev, target.pdc_id]);
    }
    setCheques((prev) => prev.filter((c) => c.key !== key));
  };

  // The add-cheque button only shows while the last row is being written
  // in - same pattern as the Fee Plan pages' Add Installment button.
  // Attached to the row (not FormInputs, which discards onFocus/onBlur
  // props in favor of its own internal handler) and relies on React's
  // synthetic focus/blur bubbling up from the actual input.
  const [isPdcLastRowActive, setIsPdcLastRowActive] = useState(false);
  const pdcLastRowBlurTimeoutRef = useRef(null);
  const handlePdcLastRowFocus = () => {
    if (pdcLastRowBlurTimeoutRef.current) clearTimeout(pdcLastRowBlurTimeoutRef.current);
    setIsPdcLastRowActive(true);
  };
  const handlePdcLastRowBlur = () => {
    pdcLastRowBlurTimeoutRef.current = setTimeout(() => setIsPdcLastRowActive(false), 200);
  };

  const handleChequeField = (key, field, value) => {
    setCheques((prev) =>
      prev.map((c) => {
        if (c.key !== key) return c;
        const updated = { ...c, [field]: value };
        // Re-pointing a cheque at another installment re-derives its
        // checkbox: ticked only if that installment is selected for payment.
        if (field === "installment_no" && c.status !== 2) {
          updated.selected = selectedInstallmentNumbers.has(Number(value));
        }
        return updated;
      })
    );
  };

  const handleCancel = () => {
    navigate(
      cameFrom === "record_payment_dashboard"
        ? "/record_payment_dashboard"
        : "/fee_payment_dashboard"
    );
  };

  const [formData, setFormData] = useState({
    payment_mode: null,
    payment_date: todayStr(),
    net_amount: "",
    late_fees: "",
    paid: "",
    receipt_no: "",
    transaction_ref: "",
    fee_type: null,
    remarks: "",
    sgst: "",
    cgst: "",
    bank_name: "",
  });

  const selectedInstallments = useMemo(
    () =>
      installments.filter((inst) =>
        selectedInstallmentIds.includes(inst.fee_schedule_id)
      ),
    [installments, selectedInstallmentIds]
  );

  // Fee summary shown in Student Detail Information - totals across every
  // installment of the plan (gross = net + SGST + CGST, same basis the
  // Received column uses), so Due = Gross Fees - Received Till Date.
  const feeSummary = useMemo(() => {
    const gross = installments.reduce(
      (sum, inst) =>
        sum + (Number(inst.amount) || 0) + (Number(inst.sgst) || 0) + (Number(inst.cgst) || 0),
      0
    );
    const received = installments.reduce((sum, inst) => sum + (Number(inst.received) || 0), 0);
    return { gross, received, due: Math.max(0, gross - received) };
  }, [installments]);

  // Reverse tax calculation: given a gross (tax-inclusive) figure, back
  // out its net portion using the course's SGST/CGST rates. var_tax =
  // 1 + (sgst%+cgst%)/100; net = gross / var_tax.
  const varTax = 1 + (courseFeeRates.sgst_prcnt + courseFeeRates.cgst_prcnt) / 100;

  // Net Amount Payable and Net Amount Paid both default to the remaining
  // balance on the selected installment(s) - gross minus whatever's
  // already been received, reverse-calculated down to net - but stay
  // freely editable afterward for a partial (or any other) amount,
  // unless a post dated cheque is linked (see the effect below, which
  // then takes over).
  useEffect(() => {
    if (selectedInstallments.length === 0) {
      setFormData((prev) => ({ ...prev, net_amount: "", paid: "" }));
      return;
    }
    const totals = selectedInstallments.reduce(
      (acc, inst) => ({
        gross:
          acc.gross +
          (Number(inst.amount) || 0) +
          (Number(inst.sgst) || 0) +
          (Number(inst.cgst) || 0),
        received: acc.received + (Number(inst.received) || 0),
      }),
      { gross: 0, received: 0 }
    );
    const remainingGross = Math.max(0, totals.gross - totals.received);
    const remainingNet = varTax > 0 ? remainingGross / varTax : remainingGross;
    setFormData((prev) => ({
      ...prev,
      net_amount: remainingNet,
      paid: remainingNet,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedInstallmentIds, courseFeeRates]);

  // A selected + filled-in post dated cheque "links" to this payment -
  // when one exists, Payment Mode locks to Cheque and Transaction/Ref
  // No, Bank Name, and Net Amount Paid all autofill from it (its amount
  // is gross/post-tax, reverse-calculated to a net figure the same way
  // as above). If more than one cheque is selected, the first one (by
  // row order) is used - Payment Detail only has one set of these
  // fields, not one per installment.
  //
  // The cheque only links if the installment it's against is actually
  // selected for payment (ticking a cheque's checkbox alone doesn't pull
  // its details into Payment Detail), and Canceled/Rejected cheques never
  // link.
  const linkedPdc = cheques.find(
    (c) =>
      c.selected &&
      c.amount &&
      c.bank_id &&
      c.status !== 3 &&
      c.status !== 4 &&
      selectedInstallmentNumbers.has(Number(c.installment_no))
  );

  useEffect(() => {
    if (!linkedPdc) return;
    const bank = bankList.find((b) => b.bank_id === Number(linkedPdc.bank_id));
    const matchingInst = installments.find(
      (inst) => Number(inst.installment_no) === Number(linkedPdc.installment_no)
    );
    // A cheque can have been written before an earlier partial payment,
    // so its face value may exceed what's actually still owed now - cap
    // at the remaining balance rather than always using the full cheque.
    const remainingGross = matchingInst
      ? Math.max(
          0,
          (Number(matchingInst.amount) || 0) +
            (Number(matchingInst.sgst) || 0) +
            (Number(matchingInst.cgst) || 0) -
            (Number(matchingInst.received) || 0)
        )
      : Number(linkedPdc.amount) || 0;
    const effectiveGross = Math.min(Number(linkedPdc.amount) || 0, remainingGross);
    setFormData((prev) => ({
      ...prev,
      payment_mode: 2, // Cheque
      transaction_ref: linkedPdc.cheque_no || "",
      bank_name: bank?.bank_name || "",
      paid: varTax > 0 ? effectiveGross / varTax : effectiveGross,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkedPdc?.key, linkedPdc?.amount, linkedPdc?.cheque_no, linkedPdc?.bank_id, installments]);

  // When a linked cheque gets unlinked (its checkbox unticked to pay the
  // installment without it, or its installment deselected), undo what it
  // autofilled - mode/cheque no/bank go back to blank and Net Amount Paid
  // back to the full remaining balance - instead of leaving stale cheque
  // details behind on what's now a non-cheque payment.
  const prevLinkedPdcKeyRef = useRef(null);
  useEffect(() => {
    const currentKey = linkedPdc?.key ?? null;
    if (prevLinkedPdcKeyRef.current && !currentKey) {
      setFormData((prev) => ({
        ...prev,
        payment_mode: null,
        transaction_ref: "",
        bank_name: "",
        paid: prev.net_amount,
      }));
    }
    prevLinkedPdcKeyRef.current = currentKey;
  }, [linkedPdc?.key]);

  // Tax Payable is derived from Net Amount Paid, not tracked as its own
  // state - computed forward using the course's tax rates. This
  // intentionally always uses the rate (no "same as installment, use
  // its own stored tax" shortcut) so the displayed figure exactly
  // matches what the save payload sums to - the payload allocates by
  // each installment's remaining balance, not its full original
  // amount, so a partially-paid installment's own stored sgst/cgst no
  // longer corresponds to what's actually being collected on it now.
  const paidNum = Number(formData.paid) || 0;
  const taxPayable = (paidNum * (courseFeeRates.sgst_prcnt + courseFeeRates.cgst_prcnt)) / 100;

  const totalPayable = paidNum + (Number(formData.late_fees) || 0) + taxPayable;
  const dueAmount = (Number(formData.net_amount) || 0) - paidNum;

  // How much of the selected installment(s) remaining balance is left to
  // collect, in net terms - same reverse-tax figure the "paid" default
  // above uses. Paying more than this triggers the overpayment warning
  // below instead of silently inflating every selected installment.
  const totalRemainingNet = selectedInstallments.reduce((sum, inst) => {
    const gross = (Number(inst.amount) || 0) + (Number(inst.sgst) || 0) + (Number(inst.cgst) || 0);
    const remainingGross = Math.max(0, gross - (Number(inst.received) || 0));
    return sum + (varTax > 0 ? remainingGross / varTax : remainingGross);
  }, 0);
  const excessNet = Math.max(0, paidNum - totalRemainingNet);

  // The installment the excess gets adjusted into if the person confirms
  // the overpayment warning - the next unselected, not-yet-fully-paid
  // installment after the highest selected one.
  const nextInstallmentForExcess =
    excessNet > 0.01 && selectedInstallments.length > 0
      ? installments
          .filter((inst) => {
            const gross = (Number(inst.amount) || 0) + (Number(inst.sgst) || 0) + (Number(inst.cgst) || 0);
            const received = Number(inst.received) || 0;
            return (
              inst.installment_no >
                Math.max(...selectedInstallments.map((i) => i.installment_no)) &&
              !selectedInstallmentIds.includes(inst.fee_schedule_id) &&
              received < gross
            );
          })
          .sort((a, b) => a.installment_no - b.installment_no)[0] || null
      : null;

  // Same test the backend uses to flip an installment to PAID: what's been
  // received covers its gross (net + SGST + CGST), within a paisa of rounding.
  const isInstallmentPaid = (inst) => {
    const gross = (Number(inst.amount) || 0) + (Number(inst.sgst) || 0) + (Number(inst.cgst) || 0);
    return (Number(inst.received) || 0) >= gross - 0.01;
  };

  // Installments are paid in order: one can only be paid once every earlier
  // installment is fully paid - or is being paid in this same payment (it's
  // selected too; the amount is then spread earliest-first). Returns the
  // first earlier installment standing in the way, or undefined.
  const getBlockingInstallment = (inst, selectedIds) =>
    [...installments]
      .sort((a, b) => Number(a.installment_no) - Number(b.installment_no))
      .find(
        (prev) =>
          Number(prev.installment_no) < Number(inst.installment_no) &&
          !isInstallmentPaid(prev) &&
          !selectedIds.includes(prev.fee_schedule_id)
      );

  const handleToggleInstallment = (feeScheduleId) => {
    const inst = installments.find((i) => i.fee_schedule_id === feeScheduleId);
    if (!inst) return;

    // Unticking an installment also unticks every later one that was
    // selected - they'd otherwise be left paying without their predecessor.
    if (selectedInstallmentIds.includes(feeScheduleId)) {
      setSelectedInstallmentIds((prev) =>
        prev.filter((id) => {
          if (id === feeScheduleId) return false;
          const other = installments.find((i) => i.fee_schedule_id === id);
          return !(other && Number(other.installment_no) > Number(inst.installment_no));
        })
      );
      return;
    }

    const blocking = getBlockingInstallment(inst, selectedInstallmentIds);
    if (blocking) {
      setNoTimeSet(false);
      showMessage(
        `Installment ${blocking.installment_no} is not paid yet. Installments must be paid in order - please pay installment ${blocking.installment_no} first.`
      );
      return;
    }
    setSelectedInstallmentIds((prev) => [...prev, feeScheduleId]);
  };

  const handlePaymentModeChange = (val) => {
    setFormData((prev) => ({
      ...prev,
      payment_mode: val,
      // Bank Name/Transaction No don't apply to these modes - clear any
      // stale value instead of leaving them disabled-but-populated.
      bank_name: BANK_NAME_DISABLED_MODES.includes(val) ? "" : prev.bank_name,
      transaction_ref: TRANSACTION_NO_DISABLED_MODES.includes(val) ? "" : prev.transaction_ref,
    }));
  };

  const isBankNameDisabled = BANK_NAME_DISABLED_MODES.includes(
    formData.payment_mode
  );
  const isTransactionNoDisabled = TRANSACTION_NO_DISABLED_MODES.includes(
    formData.payment_mode
  );

  // Cheque / Demand Draft use the same underlying field as
  // Transaction/Reference No. - just relabeled per the selected mode.
  const referenceFieldLabel =
    formData.payment_mode === 2
      ? "Cheque Number"
      : formData.payment_mode === 6
      ? "Demand Draft Number"
      : "Transaction / Reference No.";

  const showMessage = (text) => {
    setIsMessageBoxVisible(true);
    setMessageBox(
      <div className="Container-messageBOX">
        <div className="messagebox-insider1">
          <div className="loader-tc"></div>
          <span style={{ marginLeft: "10px", marginTop: "3px" }}>
            {text}
          </span>
        </div>
      </div>
    );
    setActiveImg(false);
  };

  // Same message box as showMessage, but with OK/Cancel buttons for
  // cases (like the overpayment warning) that need the person's
  // explicit go-ahead - the native browser confirm() looked like a
  // localhost warning popup rather than part of the app.
  const showConfirm = (text, onOk) => {
    setIsMessageBoxVisible(true);
    setMessageBox(
      <div className="Container-messageBOX">
        <div className="messagebox-insider1">
          <span style={{ marginLeft: "10px", marginTop: "3px" }}>{text}</span>
        </div>
        <div style={{ display: "flex", gap: "10px", marginTop: "10px", marginLeft: "10px" }}>
          <button
            onClick={() => {
              setIsMessageBoxVisible(false);
              onOk();
            }}
            style={{
              padding: "6px 16px",
              border: "1px solid #ccc",
              borderRadius: "4px",
              background: "#fff",
              cursor: "pointer",
            }}
          >
            Yes
          </button>
          <button
            onClick={() => setIsMessageBoxVisible(false)}
            style={{
              padding: "6px 16px",
              border: "1px solid #ccc",
              borderRadius: "4px",
              background: "#fff",
              cursor: "pointer",
            }}
          >
            No
          </button>
        </div>
      </div>
    );
    setActiveImg(false);
  };

  const groupedPaymentHistory = useMemo(() => {
    const groups = [];
    const byKey = new Map();
    paymentHistory.forEach((payment) => {
      const key = [
        payment.payment_date,
        payment.payment_mode_id,
        payment.transaction_no,
        payment.received_by,
        payment.received_on,
      ].join("|");
      if (!byKey.has(key)) {
        const group = {
          ...payment,
          key,
          payment_ids: [],
          amount: 0,
          sgst: 0,
          cgst: 0,
          due_amt: 0,
        };
        byKey.set(key, group);
        groups.push(group);
      }
      const group = byKey.get(key);
      group.payment_ids.push(payment.payment_id);
      group.amount += Number(payment.amount) || 0;
      group.sgst += Number(payment.sgst) || 0;
      group.cgst += Number(payment.cgst) || 0;
      group.due_amt += Number(payment.due_amt) || 0;
    });
    return groups;
  }, [paymentHistory]);

  // Entering edit mode snapshots the current grouped view into an
  // editable draft - editing (and saving) then operates entirely on
  // groups, never individual installment rows, matching what's shown.
  const toggleEditingHistory = () => {
    setIsEditingHistory((prev) => {
      const next = !prev;
      if (next) {
        const draft = JSON.parse(JSON.stringify(groupedPaymentHistory));
        setEditableHistoryGroups(draft);
        setPristineHistoryGroups(JSON.parse(JSON.stringify(draft)));
      }
      return next;
    });
  };

  const handleGroupFieldChange = (idx, field, value) => {
    setEditableHistoryGroups((prev) =>
      prev.map((group, i) => (i === idx ? { ...group, [field]: value } : group))
    );
  };

  // Drives whether the single Save button is enabled at all, and which
  // of the (eventually three separate) POST calls it needs to make.
  const isPaymentDirty = selectedInstallmentIds.length > 0;
  const isPdcDirty =
    pendingDeletePdcIds.length > 0 ||
    (pristineCheques !== null &&
      JSON.stringify(cheques.map(({ selected, ...rest }) => rest)) !==
        JSON.stringify(pristineCheques.map(({ selected, ...rest }) => rest)));
  const isHistoryDirty =
    pristineHistoryGroups !== null &&
    JSON.stringify(editableHistoryGroups) !== JSON.stringify(pristineHistoryGroups);
  const canSave = isPaymentDirty || isPdcDirty || isHistoryDirty;

  // A single payment that covered multiple installments (several
  // selected at once, or an overpayment rolled into the next
  // installment) is stored as one Student_Fee_Payment row per
  // installment - group those back into one entry, in both the
  // read-only view and while editing, so the history section always
  // shows it as the single payment it actually was. Editing a group's
  // Amount sends the new total to the backend, which redistributes it
  // across the group's underlying rows (see
  // update_grouped_student_fee_payment_history).

  // Handles "Payments" and, for now, "Post Dated Cheques" together - the
  // backend only has one endpoint today (create-student-fee-payment) and
  // it requires at least one installment per call, so PDC-only saves
  // aren't actually possible yet. Once the three separate POST APIs
  // exist (payment history / PDC / payment), this should only cover
  // payment, with PDC split into its own function calling its own
  // endpoint.
  // Every pre-flight check for the payment, returning the error message
  // (or null when it's fine). Kept separate from savePayment so handleSave
  // can run it BEFORE anything is sent - otherwise the post dated cheques
  // would already be saved by the time a payment check fails, and the next
  // Save would add those same cheques a second time.
  const getPaymentValidationError = () => {
    if (selectedInstallmentIds.length === 0) {
      return "Please select at least one installment to record payment for.";
    }

    // Installments must be paid in order (the checkbox handler already
    // enforces this - re-checked here so a save can never slip past it).
    for (const inst of selectedInstallments) {
      const blocking = getBlockingInstallment(inst, selectedInstallmentIds);
      if (blocking) {
        return `Installment ${inst.installment_no} can't be paid until installment ${blocking.installment_no} is paid. Installments must be paid in order.`;
      }
    }

    // A post dated cheque can only be used for a payment made on or after
    // its cheque date (today >= cheque date). Checked for every cheque
    // ticked against a selected installment - untick the cheque to pay
    // that installment without it.
    const today = localTodayStr();
    const earlyCheque = cheques.find(
      (c) =>
        c.selected &&
        c.status !== 2 &&
        c.status !== 3 &&
        c.status !== 4 &&
        selectedInstallmentNumbers.has(Number(c.installment_no)) &&
        c.cheque_date &&
        String(c.cheque_date).slice(0, 10) > today
    );
    if (earlyCheque) {
      return `Post dated cheque${
        earlyCheque.cheque_no ? ` no. ${earlyCheque.cheque_no}` : ""
      } (installment ${earlyCheque.installment_no}) is dated ${formatDisplayDate(
        earlyCheque.cheque_date
      )}. Payment can only be made on or after the cheque date - uncheck the cheque to pay this installment without it.`;
    }

    if (!formData.payment_date) return "Please select a Payment Date.";
    if (!formData.payment_mode) return "Please select a Payment Mode.";
    if (!isTransactionNoDisabled && !formData.transaction_ref) {
      return `${referenceFieldLabel} is mandatory for non-cash payment modes.`;
    }
    if (!formData.receipt_no?.trim()) return "Receipt Number is mandatory.";
    if (!formData.paid) return "Please enter the amount being paid.";
    if (!userName) {
      return "Unable to determine the logged-in user. Please re-login and try again.";
    }
    return null;
  };

  const savePayment = async (excessAdjustment) => {
    const validationError = getPaymentValidationError();
    if (validationError) {
      return { success: false, message: validationError };
    }

    try {
      const token = localStorage.getItem("other");

      const response = await fetch(EndPoints.CREATE_STUDENT_FEE_PAYMENT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          student_id: Number(studentId),
          payment_date: formData.payment_date
            ? new Date(formData.payment_date).toISOString()
            : new Date().toISOString(),
          payment_mode_id: formData.payment_mode?.toString() || "",
          transaction_no: formData.transaction_ref || "",
          bank_name: formData.bank_name || "",
          receipt_no: formData.receipt_no || null,
          remarks: formData.remarks || "",
          received_by: userName || "",
          installments: (() => {
            const remainingByInst = selectedInstallments.map((inst) => {
              const gross =
                (Number(inst.amount) || 0) + (Number(inst.sgst) || 0) + (Number(inst.cgst) || 0);
              const remainingGross = Math.max(0, gross - (Number(inst.received) || 0));
              const remainingNet = varTax > 0 ? remainingGross / varTax : remainingGross;
              return { inst, remainingGross, remainingNet };
            });

            // due_amt is the gross balance still outstanding on that
            // installment after this entry's payment - previously
            // hardcoded to 0, so payment history always showed no due
            // even when only part of the installment had been paid.
            const toEntry = (feeScheduleId, netAmount, remainingGross) => ({
              fee_schedule_id: feeScheduleId,
              amount: netAmount,
              sgst: (netAmount * courseFeeRates.sgst_prcnt) / 100,
              cgst: (netAmount * courseFeeRates.cgst_prcnt) / 100,
              due_amt: Math.max(0, remainingGross - netAmount * varTax),
            });

            // Confirmed overpayment: pay each selected installment exactly
            // its remaining balance (never more), then park the excess
            // against the next installment instead of scaling every
            // selected installment above what it actually owes.
            if (excessAdjustment) {
              const entries = remainingByInst.map(({ inst, remainingNet, remainingGross }) =>
                toEntry(inst.fee_schedule_id, remainingNet, remainingGross)
              );
              const nextInst = excessAdjustment.nextInst;
              const nextGross =
                (Number(nextInst.amount) || 0) + (Number(nextInst.sgst) || 0) + (Number(nextInst.cgst) || 0);
              const nextRemainingGross = Math.max(0, nextGross - (Number(nextInst.received) || 0));
              entries.push(
                toEntry(nextInst.fee_schedule_id, excessAdjustment.excessNet, nextRemainingGross)
              );
              return entries;
            }

            // Not an overpayment: fill the selected installments in
            // order (earliest first) up to what each actually owes,
            // instead of splitting the amount proportionally across all
            // of them - paying 30,000 against two 20,000 installments
            // should fully clear the first and put 10,000 toward the
            // second, not split 15,000/15,000 across both.
            let remainingPaid = paidNum;
            return remainingByInst.map(({ inst, remainingNet, remainingGross }) => {
              const applied = Math.max(0, Math.min(remainingPaid, remainingNet));
              remainingPaid -= applied;
              return toEntry(inst.fee_schedule_id, applied, remainingGross);
            });
          })(),
        }),
      });
      const data = await response.json();

      return {
        success: data?.status === 200,
        message:
          data?.status === 200
            ? "Payment recorded successfully."
            : data?.message || "Failed to record payment.",
      };
    } catch (error) {
      return { success: false, message: "Something went wrong while recording the payment." };
    }
  };

  const savePostDatedCheques = async () => {
    if (!userName) {
      return { success: false, message: "Unable to determine the logged-in user. Please re-login and try again." };
    }

    // Existing rows (already saved, have a pdc_id) get updated whenever
    // their data actually changed from what was loaded - independent of
    // the selected checkbox, since correcting an existing cheque's
    // details isn't the same action as selecting a new one to submit
    // alongside today's payment.
    const editedExisting = cheques.filter((c) => {
      if (!c.pdc_id) return false;
      const original = pristineCheques?.find((p) => p.pdc_id === c.pdc_id);
      if (!original) return false;
      const { selected: _s1, ...cRest } = c;
      const { selected: _s2, ...origRest } = original;
      return JSON.stringify(cRest) !== JSON.stringify(origRest);
    });

    // New rows (no pdc_id yet) are saved as soon as the person has
    // entered any cheque detail on them (bank, cheque no, date or branch)
    // - the checkbox is NOT required. It only decides which cheque links
    // to today's payment; every cheque filled in gets saved either way.
    // Installment no. and amount come prefilled, so they don't count as
    // "filled in" - untouched default rows are simply skipped.
    const hasChequeDetails = (c) =>
      !!(c.bank_id || c.cheque_no || c.cheque_date || (c.branch_name || "").trim());
    const newFilled = cheques.filter((c) => !c.pdc_id && hasChequeDetails(c));

    const missingInstallmentNo = [...editedExisting, ...newFilled].find(
      (c) => !c.installment_no
    );
    if (missingInstallmentNo) {
      return {
        success: false,
        message: "Every post dated cheque needs an installment number - please fill it in before saving.",
      };
    }

    const missingChequeDetails = [...editedExisting, ...newFilled].find(
      (c) => !c.cheque_no || !c.cheque_date || !c.bank_id || !(Number(c.amount) > 0)
    );
    if (missingChequeDetails) {
      return {
        success: false,
        message: "Every post dated cheque needs an amount, cheque number, cheque date and bank - please fill them in before saving.",
      };
    }

    // Post dated cheques on file (pending or realized) can never add up
    // to more than the fee plan's total, taxes included - guards against
    // over-committing cheques beyond what's actually owed.
    const totalFeeGross = installments.reduce(
      (sum, inst) =>
        sum + (Number(inst.amount) || 0) + (Number(inst.sgst) || 0) + (Number(inst.cgst) || 0),
      0
    );
    // Only cheques that are (or are about to be) saved count - untouched
    // default rows with a prefilled amount aren't being committed.
    const totalPdcAmount = cheques
      .filter((c) => c.status !== 3 && c.status !== 4) // exclude Canceled/Rejected
      .filter((c) => c.pdc_id || hasChequeDetails(c))
      .reduce((sum, c) => sum + (Number(c.amount) || 0), 0);
    if (totalPdcAmount > totalFeeGross) {
      return {
        success: false,
        message: `Total post dated cheque amount (₹${formatINR(totalPdcAmount)}) cannot exceed the total fee including taxes (₹${formatINR(totalFeeGross)}). Please adjust the cheque amount(s).`,
      };
    }

    if (
      editedExisting.length === 0 &&
      newFilled.length === 0 &&
      pendingDeletePdcIds.length === 0
    ) {
      return { success: true, message: "" };
    }

    const token = localStorage.getItem("other");
    const results = [];

    try {
      if (newFilled.length > 0) {
        const chequesToSubmit = newFilled.map((c) => ({
          installment_no: Number(c.installment_no),
          amount: Number(c.amount) || 0,
          cheque_no: c.cheque_no || null,
          cheque_date: c.cheque_date ? new Date(c.cheque_date).toISOString() : null,
          bank_id: Number(c.bank_id),
          branch_name: c.branch_name || null,
          status: Number(c.status) || 1,
        }));

        const response = await fetch(EndPoints.CREATE_POST_DATED_CHEQUES, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            student_id: Number(studentId),
            created_by: userName || "",
            post_dated_cheques: chequesToSubmit,
          }),
        });
        const data = await response.json();
        results.push({
          success: data?.status === 200,
          message:
            data?.status === 200
              ? `${chequesToSubmit.length} new post dated cheque(s) recorded.`
              : data?.message || "Failed to record new post dated cheques.",
        });
      }

      if (editedExisting.length > 0) {
        const updates = editedExisting.map((c) => ({
          pdc_id: c.pdc_id,
          installment_no: Number(c.installment_no),
          amount: Number(c.amount) || 0,
          cheque_no: c.cheque_no || null,
          cheque_date: c.cheque_date ? new Date(c.cheque_date).toISOString() : null,
          bank_id: Number(c.bank_id),
          branch_name: c.branch_name || null,
          status: Number(c.status) || 1,
        }));

        const response = await fetch(EndPoints.UPDATE_POST_DATED_CHEQUES, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ updates }),
        });
        const data = await response.json();
        results.push({
          success: data?.status === 200,
          message:
            data?.status === 200
              ? "Post dated cheques updated successfully."
              : data?.message || "Failed to update post dated cheques.",
        });
      }

      if (pendingDeletePdcIds.length > 0) {
        const deleteResults = await Promise.all(
          pendingDeletePdcIds.map((pdcId) =>
            fetch(`${EndPoints.DELETE_POST_DATED_CHEQUE}${pdcId}`, {
              method: "DELETE",
              headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
            }).then((r) => r.json())
          )
        );
        const allDeleted = deleteResults.every((d) => d?.status === 200);
        results.push({
          success: allDeleted,
          message: allDeleted
            ? `${pendingDeletePdcIds.length} post dated cheque(s) removed.`
            : "Failed to remove one or more post dated cheques.",
        });
        if (allDeleted) {
          setPendingDeletePdcIds([]);
        }
      }

      return {
        success: results.every((r) => r.success),
        message: results.map((r) => r.message).filter(Boolean).join(" "),
      };
    } catch (error) {
      return { success: false, message: "Something went wrong while saving the post dated cheques." };
    }
  };

  const saveHistoryChanges = async () => {
    // Only groups that actually changed from what was originally loaded
    // are sent - not the whole table.
    const changedGroups = editableHistoryGroups.filter((group, idx) => {
      const original = pristineHistoryGroups?.[idx];
      return original && JSON.stringify(group) !== JSON.stringify(original);
    });

    if (changedGroups.length === 0) {
      return { success: true, message: "" };
    }

    const missingPaymentIds = changedGroups.find(
      (group) => !group.payment_ids || group.payment_ids.length === 0
    );
    if (missingPaymentIds) {
      return {
        success: false,
        message: "One or more edited payment records are missing their payment_id(s) - can't save.",
      };
    }

    try {
      const token = localStorage.getItem("other");

      const response = await fetch(EndPoints.UPDATE_GROUPED_STUDENT_FEE_PAYMENT_HISTORY, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          updates: changedGroups.map((group) => ({
            payment_ids: group.payment_ids,
            payment_date: group.payment_date ? new Date(group.payment_date).toISOString() : null,
            amount: Number(group.amount) || 0,
            payment_mode_id: group.payment_mode_id?.toString() || "",
            transaction_no: group.transaction_no || null,
            bank_name: group.bank_name || null,
            received_by: group.received_by || "",
            remarks: group.remarks || null,
            received_on: group.received_on ? new Date(group.received_on).toISOString() : null,
          })),
        }),
      });
      const data = await response.json();

      if (data?.status === 200) {
        setIsEditingHistory(false);
      }

      return {
        success: data?.status === 200,
        message:
          data?.status === 200
            ? "Payment history updated successfully."
            : data?.message || "Failed to update payment history.",
      };
    } catch (error) {
      return { success: false, message: "Something went wrong while updating payment history." };
    }
  };

  const proceedSave = async (excessAdjustment) => {
    setIsSaving(true);

    // Each step only runs if the one before it succeeded. Before, a
    // failed cheque save (e.g. a missing cheque date) still let the
    // payment go through, and since the page doesn't reload after a
    // failure, pressing Save again recorded that payment a second time.
    const results = [];
    let canContinue = true;
    if (canContinue && isPdcDirty) {
      const r = await savePostDatedCheques();
      results.push(r);
      canContinue = r.success;
    }
    if (canContinue && isPaymentDirty) {
      const r = await savePayment(excessAdjustment);
      results.push(r);
      canContinue = r.success;
    }
    if (canContinue && isHistoryDirty) {
      results.push(await saveHistoryChanges());
    }

    const allSucceeded = results.every((r) => r.success);
    const combinedMessage = results.map((r) => r.message).filter(Boolean).join(" ");

    setNoTimeSet(false);
    setIsMessageBoxVisible(true);
    setMessageBox(
      <div className="Container-messageBOX">
        <div className="messagebox-insider1">
          <div className="loader-tc"></div>
          <span>{combinedMessage}</span>
        </div>
      </div>
    );
    setActiveImg(false);

    // All three save paths now hit real endpoints, so a reload after any
    // successful save is safe - it'll re-fetch whatever was actually
    // persisted rather than risk showing stale local state.
    if (allSucceeded) {
      if (isPaymentDirty) {
        // A payment was recorded - go back to the fee payment dashboard,
        // but only after the success message has been on screen for a
        // moment. The dashboard replaces whatever message is showing with
        // its own "Loading data..." one the instant it opens (and hides the
        // box when it's done), so navigating straight away swallows it.
        setTimeout(() => {
          handleCancel();
        }, 1500);
      } else {
        // Only cheques / history edits were saved - stay and refresh.
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      }
      // Save stays disabled until we leave / reload - re-enabling it here
      // would let a second click record the same payment again.
      return;
    }

    setIsSaving(false);
  };

  const handleSave = async () => {
    if (!studentId) {
      showMessage("No student is selected. Please go back and select a student.");
      return;
    }
    if (!canSave) return;

    // Payment checks (incl. cheque date not in the future) run first, before
    // the overpayment prompt and before any cheque/payment is sent.
    if (isPaymentDirty) {
      const paymentError = getPaymentValidationError();
      if (paymentError) {
        setNoTimeSet(false);
        showMessage(paymentError);
        return;
      }
    }

    // Paying more than what's due on the selected installment(s) needs
    // the person's explicit go-ahead before anything is sent - shown in
    // the app's own message box rather than a native browser confirm()
    // - and a place to park the excess - rather than silently inflating
    // every selected installment's paid amount.
    if (isPaymentDirty && excessNet > 0.01) {
      if (!nextInstallmentForExcess) {
        showMessage(
          "The amount being paid is more than the selected installment's fee, and there is no later installment to adjust the excess into. Please reduce the amount being paid."
        );
        return;
      }
      showConfirm(
        `You are paying ₹${formatINR(excessNet)} more than the selected installment(s) fee. The excess will be adjusted against installment #${nextInstallmentForExcess.installment_no}. Do you still want to continue?`,
        () => proceedSave({ nextInst: nextInstallmentForExcess, excessNet })
      );
      return;
    }

    proceedSave(null);
  };

  return (
    <div className="create-role-container">
      {/* Header */}
      <div className="create-role-container-ques-paper-del">
        <span className="create-role-heading">Fee Collection</span>
        <div className="cr-header-btn-container">
         <button className="cr-cancel-btn" onClick={handleCancel} >
           CANCEL
          </button>
          <button className="cr-create-btn" onClick={handleSave} disabled={isSaving || !canSave}>
            {isSaving ? "SAVING..." : "SAVE"}
          </button>
        </div>
      </div>

      {/* STUDENT DETAIL INFORMATION */}
      <div className="ques-paper-del">
        <div className="personal-header">
          <div className="header">
            <label>STUDENT DETAIL INFORMATION</label>
          </div>
          <div className="accordion-btn" onClick={() => setIsOpenStudent((p) => !p)}>
            {isOpenStudent ? <div className="minus"></div> : <div className="plus">+</div>}
          </div>
        </div>
        {isOpenStudent && (
          <div className="accordion-content">
            <div className="select-assign-test-container-ques-pap-del">
              <div className="grid-container-ques-paper-del">
                <div className="item1">
                  <FormInputs
                    label="Student Name"
                    value={studentProfile?.student_full_name || ""}
                    disabled
                  />
                </div>
                <div className="item2">
                  <FormInputs
                    label="Roll Number"
                    value={studentProfile?.Roll_Number || ""}
                    disabled
                  />
                </div>
                <div className="item3">
                  <FormInputs
                    label="Course"
                    value={studentProfile?.Course_Name || ""}
                    disabled
                  />
                </div>
                <div className="item4">
                  <FormInputs
                    label="Batch"
                    value={studentProfile?.Batch || ""}
                    disabled
                  />
                </div>
                <div className="item5">
                  <FormInputs
                    label="Class"
                    value={studentProfile?.Class_name || ""}
                    disabled
                  />
                </div>
                <div className="item6">
                  <FormInputs
                    label="Date of admission"
                    value={formatDisplayDate(studentProfile?.admission_date)}
                    disabled
                  />
                </div>
                <div className="item1">
                  <FormInputs
                    label="Session"
                    value={studentProfile?.session || ""}
                    disabled
                  />
                </div>
                <div className="item2">
                  <FormInputs
                    label="Father/Mother/Guardian"
                    value={
                      studentProfile?.father_name ||
                      studentProfile?.mother_name ||
                      studentProfile?.guardian_name ||
                      ""
                    }
                    disabled
                  />
                </div>
                <div className="item3">
                  <FormInputs
                    label="Account Student ID"
                    value={accStudentId}
                    placeholder="Enter Ledger / Account ID"
                    maxLength={10}
                    onChange={(e) => setAccStudentId(e.target.value.replace(/\D/g, ""))}
                  />
                </div>
                <div className="item4">
                  <FormInputs
                    label="Gross Fees (₹)"
                    value={installments.length > 0 ? formatINR(feeSummary.gross) : ""}
                    disabled
                  />
                </div>
                <div className="item5">
                  <FormInputs
                    label="Received Till Date (₹)"
                    value={installments.length > 0 ? formatINR(feeSummary.received) : ""}
                    disabled
                  />
                </div>
                <div className="item6">
                  <FormInputs
                    label="Due (₹)"
                    value={installments.length > 0 ? formatINR(feeSummary.due) : ""}
                    disabled
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* PAYMENT HISTORY */}
      <div className="ques-paper-del">
        <div className="personal-header">
          <div className="header" style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <label>PAYMENT HISTORY</label>
            {paymentHistory.length > 0 && (
              <img
                src={PencilIcon}
                alt="Edit"
                className="pr-pencil-icon"
                onClick={toggleEditingHistory}
                title={isEditingHistory ? "Stop editing" : "Edit payment history"}
              />
            )}
          </div>
          <div className="accordion-btn" onClick={() => setIsOpenHistory((p) => !p)}>
            {isOpenHistory ? <div className="minus"></div> : <div className="plus">+</div>}
          </div>
        </div>
        {isOpenHistory && (
          <div className="accordion-content">
            {paymentHistory.length > 0 ? (
              <div className="nfp-table-wrap nfp-table-wrap-flow">
                <table className="nfp-table pr-history-table">
                  <thead>
                    <tr>
                      <th>Payment Date</th>
                      <th>Amount (₹)</th>
                      <th>SGST (₹)</th>
                      <th>CGST (₹)</th>
                      <th>Payment Mode</th>
                      <th>Transaction / Ref No.</th>
                      <th>Bank Name</th>
                      <th>Remarks</th>
                      <th>Received By</th>
                      <th>Received On</th>
                    </tr>
                  </thead>
                  <tbody>
                    {isEditingHistory
                      ? editableHistoryGroups.map((group, idx) => (
                        <tr key={group.key ?? idx}>
                          <td>
                            <FormDateInput
                              label=""
                              max={localTodayStr()}
                              value={group.payment_date || ""}
                              onChange={(e) =>
                                handleGroupFieldChange(idx, "payment_date", e.target.value)
                              }
                            />
                          </td>
                          <td>
                            <FormInputs
                              label=""
                              type="number"
                              value={group.amount ?? ""}
                              onChange={(e) =>
                                handleGroupFieldChange(idx, "amount", e.target.value)
                              }
                            />
                          </td>
                          <td>
                            <FormInputs label="" value={formatINR(group.sgst)} disabled />
                          </td>
                          <td>
                            <FormInputs label="" value={formatINR(group.cgst)} disabled />
                          </td>
                          <td>
                            <DropDown
                              selected={
                                group.payment_mode_id !== undefined && group.payment_mode_id !== ""
                                  ? Number(group.payment_mode_id)
                                  : null
                              }
                              setSelected={(val) =>
                                handleGroupFieldChange(idx, "payment_mode_id", val?.toString() || "")
                              }
                              options={PAYMENT_MODE_OPTIONS}
                              width="100%"
                              Height="auto"
                              minheight="150px"
                              OverFlow="auto"
                            />
                          </td>
                          <td>
                            <FormInputs
                              label=""
                              value={group.transaction_no || ""}
                              maxLength={100}
                              disabled={TRANSACTION_NO_DISABLED_MODES.includes(
                                Number(group.payment_mode_id)
                              )}
                              onChange={(e) =>
                                handleGroupFieldChange(idx, "transaction_no", e.target.value)
                              }
                            />
                          </td>
                          <td>
                            <DropDown
                              selected={bankList.find((b) => b.bank_name === group.bank_name)?.bank_id ?? null}
                              setSelected={(bankId) => {
                                const bank = bankList.find((b) => b.bank_id === bankId);
                                handleGroupFieldChange(idx, "bank_name", bank?.bank_name || "");
                              }}
                              options={bankList.map((b) => ({ id: b.bank_id, label: b.bank_name }))}
                              placeholder="Select bank"
                              disabled={BANK_NAME_DISABLED_MODES.includes(
                                Number(group.payment_mode_id)
                              )}
                              width="100%"
                              Height="auto"
                              minheight="150px"
                              OverFlow="auto"
                            />
                          </td>
                          <td>
                            <FormInputs
                              label=""
                              value={group.remarks || ""}
                              className="nfp-remarks-input"
                              onChange={(e) =>
                                handleGroupFieldChange(idx, "remarks", e.target.value)
                              }
                            />
                          </td>
                          <td>
                            <FormInputs
                              label=""
                              value={group.received_by_name || group.received_by || ""}
                              disabled
                            />
                          </td>
                          <td>
                            <FormDateInput
                              label=""
                              showTime
                              value={group.received_on || ""}
                              onChange={(e) =>
                                handleGroupFieldChange(idx, "received_on", e.target.value)
                              }
                            />
                          </td>
                        </tr>
                      ))
                      : groupedPaymentHistory.map((group, idx) => (
                        <tr key={group.key ?? idx}>
                          <td>{formatDisplayDate(group.payment_date, "-")}</td>
                          <td>{formatINR(group.amount)}</td>
                          <td>{formatINR(group.sgst)}</td>
                          <td>{formatINR(group.cgst)}</td>
                          <td>
                            {PAYMENT_MODE_OPTIONS.find(
                              (m) => m.id === Number(group.payment_mode_id)
                            )?.label || group.payment_mode_id}
                          </td>
                          <td>{group.transaction_no || "-"}</td>
                          <td>{group.bank_name || "-"}</td>
                          <td className="nfp-remarks-cell">{group.remarks || "-"}</td>
                          <td title={group.received_by_name || group.received_by || ""}>
                            {group.received_by_name || group.received_by || "-"}
                          </td>
                          <td>{formatDisplayDateTime(group.received_on, "-")}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="pr-no-student">No payment history found for this student.</div>
            )}
          </div>
        )}
      </div>

      {/* FEE PLAN INSTALLMENTS */}
      <div className="ques-paper-del">
        <div className="personal-header">
          <div className="header">
            <label>FEE PLAN INSTALLMENTS</label>
          </div>
          <div className="accordion-btn" onClick={() => setIsOpenInstallments((p) => !p)}>
            {isOpenInstallments ? <div className="minus"></div> : <div className="plus">+</div>}
          </div>
        </div>
        {isOpenInstallments && (
          <div className="accordion-content">
            {installments.length > 0 ? (
              <div className="nfp-table-wrap">
                <table className="nfp-table">
                  <thead>
                    <tr>
                      <th style={{ width: "40px" }}></th>
                      <th style={{ width: "60px" }}>Inst. No</th>
                      <th>Due Date</th>
                      <th>Net Amount (₹)</th>
                      <th>SGST (₹)</th>
                      <th>CGST (₹)</th>
                      <th>Gross Amount (₹)</th>
                      <th>Received (₹)</th>
                      <th>Status</th>
                      <th>Remarks</th>
                    </tr>
                  </thead>
                  <tbody>
                    {installments.map((inst) => {
                      const gross =
                        (Number(inst.amount) || 0) +
                        (Number(inst.sgst) || 0) +
                        (Number(inst.cgst) || 0);
                      const received = Number(inst.received) || 0;
                      const derivedStatus =
                        received <= 0 ? "Pending" : !isInstallmentPaid(inst) ? "Partially Paid" : "Paid";
                      return (
                      <tr key={inst.fee_schedule_id}>
                        <td style={{ textAlign: "center" }}>
                          {derivedStatus !== "Paid" && (
                            <input
                              type="checkbox"
                              checked={selectedInstallmentIds.includes(inst.fee_schedule_id)}
                              onChange={() => handleToggleInstallment(inst.fee_schedule_id)}
                            />
                          )}
                        </td>
                        <td className="nfp-inst-no">{inst.installment_no}</td>
                        <td>{formatDisplayDate(inst.due_date, "-")}</td>
                        <td>{formatINR(inst.amount)}</td>
                        <td>{formatINR(inst.sgst)}</td>
                        <td>{formatINR(inst.cgst)}</td>
                        <td>{formatINR(gross)}</td>
                        <td>{formatINR(received)}</td>
                        <td>{derivedStatus}</td>
                        <td className="nfp-remarks-cell">{inst.remarks || "-"}</td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="pr-no-student">No fee plan installments found for this student.</div>
            )}
          </div>
        )}
      </div>

      {/* POST DATED CHEQUES */}
      <div className="ques-paper-del">
        <div className="personal-header">
          <div className="header">
            <label>POST DATED CHEQUES</label>
          </div>
          <div className="accordion-btn" onClick={() => setIsOpenPdc((p) => !p)}>
            {isOpenPdc ? <div className="minus"></div> : <div className="plus">+</div>}
          </div>
        </div>
        {isOpenPdc && (
          <div className="accordion-content">
            <div className="nfp-table-wrap nfp-table-wrap-flow">
              <table className="nfp-table pr-pdc-table">
                <thead>
                  <tr>
                    <th style={{ width: "40px" }}></th>
                    <th style={{ width: "40px" }}></th>
                    <th style={{ width: "70px" }}>Inst. No</th>
                    <th style={{ width: "150px" }}>Amount (₹)</th>
                    <th>Cheque No</th>
                    <th style={{ width: "150px" }}>Cheque Date</th>
                    <th>Bank</th>
                    <th>Branch Name</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {cheques.map((c, idx) => {
                    // Realized only ever happens as a side effect of
                    // actually paying this installment/cheque through
                    // Payment Detail - never editable here directly,
                    // shown as disabled boxes rather than plain text or
                    // hidden fields.
                    const isRealized = c.status === 2;
                    const isLastRow = idx === cheques.length - 1;
                    const previousChequeDate = idx > 0 ? cheques[idx - 1].cheque_date : null;
                    const minChequeDate =
                      previousChequeDate && nextDayStr(previousChequeDate) > todayStr()
                        ? nextDayStr(previousChequeDate)
                        : todayStr();
                    return (
                      <tr
                        key={c.key}
                        onFocus={isLastRow ? handlePdcLastRowFocus : undefined}
                        onBlur={isLastRow ? handlePdcLastRowBlur : undefined}
                      >
                        <td>
                          <div className="pr-pdc-cell-center">
                            {!isRealized && (
                              <span
                                className="nfp-remove-btn"
                                onClick={() => handleRemoveCheque(c.key)}
                                title="Remove cheque"
                              >
                                ×
                              </span>
                            )}
                          </div>
                        </td>
                        <td>
                          <div className="pr-pdc-cell-center">
                            {!isRealized && (
                              <input
                                type="checkbox"
                                checked={!!c.selected}
                                onChange={(e) =>
                                  handleChequeField(c.key, "selected", e.target.checked)
                                }
                              />
                            )}
                          </div>
                        </td>
                        <td className="pr-pdc-narrow">
                          <FormInputs
                            label=""
                            type="number"
                            min="1"
                            max="32767"
                            placeholder="Inst. no"
                            value={c.installment_no}
                            disabled={isRealized}
                            onChange={(e) =>
                              handleChequeField(c.key, "installment_no", e.target.value)
                            }
                          />
                        </td>
                        <td className="pr-pdc-narrow">
                          <FormInputs
                            label=""
                            type="number"
                            min="0"
                            placeholder="0"
                            value={c.amount}
                            disabled={isRealized}
                            onChange={(e) => handleChequeField(c.key, "amount", e.target.value)}
                          />
                        </td>
                        <td>
                          <FormInputs
                            label=""
                            placeholder="Cheque no"
                            value={c.cheque_no}
                            disabled={isRealized}
                            maxLength={6}
                            onChange={(e) =>
                              handleChequeField(
                                c.key,
                                "cheque_no",
                                e.target.value.replace(/\D/g, "")
                              )
                            }
                          />
                        </td>
                        <td className="pr-pdc-narrow">
                          <FormDateInput
                            label=""
                            min={minChequeDate}
                            max={sessionEndDate || "2100-12-31"}
                            value={c.cheque_date}
                            disabled={isRealized}
                            onChange={(e) =>
                              handleChequeField(c.key, "cheque_date", e.target.value)
                            }
                          />
                        </td>
                        <td>
                          <DropDown
                            selected={c.bank_id}
                            setSelected={(val) => handleChequeField(c.key, "bank_id", val)}
                            options={bankList.map((b) => ({ id: b.bank_id, label: b.bank_name }))}
                            placeholder="Select bank"
                            disabled={isRealized}
                            width="100%"
                            Height="auto"
                            minheight="150px"
                            OverFlow="auto"
                          />
                        </td>
                        <td>
                          <FormInputs
                            label=""
                            placeholder="Branch name"
                            value={c.branch_name}
                            disabled={isRealized}
                            maxLength={100}
                            onChange={(e) =>
                              handleChequeField(c.key, "branch_name", e.target.value)
                            }
                          />
                        </td>
                        <td>
                          <DropDown
                            selected={c.status}
                            setSelected={(val) => handleChequeField(c.key, "status", val)}
                            options={getPdcStatusOptions(c.status)}
                            disabled={isRealized}
                            width="100%"
                            Height="auto"
                            minheight="150px"
                            OverFlow="auto"
                          />
                        </td>
                      </tr>
                    );
                  })}
                  {(isPdcLastRowActive ||
                    (cheques.length > 0 && cheques.every((c) => c.status === 2))) && (
                    <tr className="nfp-add-installment-row-inline">
                      <td colSpan={8}></td>
                      <td style={{ textAlign: "right" }}>
                        <img
                          src={YellowPlus}
                          alt="Add cheque"
                          className="nfp-add-icon"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={handleAddCheque}
                          title="Add cheque"
                        />
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* PAYMENT DETAIL */}
      <div className="ques-paper-del">
        <div className="personal-header">
          <div className="header">
            <label>PAYMENT DETAIL</label>
          </div>
          <div className="accordion-btn" onClick={() => setIsOpenPayment((p) => !p)}>
            {isOpenPayment ? <div className="minus"></div> : <div className="plus">+</div>}
          </div>
        </div>
        {isOpenPayment && (
          <div className="accordion-content">
            <div className="select-assign-test-container-ques-pap-del">
              <div className="grid-container-ques-paper-del">

                <div className="item1">
                  <FormInputs
                    label="Net Amount Payable(₹)"
                    value={formData.net_amount}
                    disabled
                  />
                </div>
                <div className="item2">
                  <FormInputs
                    label="Net Amount Paid (₹)"
                    type="number"
                    value={formData.paid}
                    onChange={(e) => setFormData({ ...formData, paid: e.target.value })}
                    required
                  />
                </div>
                <div className="item3">
                  <FormInputs
                    label="Late Fees (₹)"
                    type="number"
                    value={formData.late_fees}
                    placeholder="0"
                    onChange={(e) => setFormData({ ...formData, late_fees: e.target.value })}
                  />
                </div>

                <div className="item4">
                  <FormInputs
                    label="Tax Payable (₹)"
                    value={`${taxPayable.toLocaleString("en-IN")}`}
                    disabled
                  />
                </div>
                <div className="item6">
                  <FormInputs
                    label="Total Payable (₹)"
                    value={`${totalPayable.toLocaleString("en-IN")}`}
                    disabled
                  />
                </div>

                <div className="item1">
                  <FormInputs
                    label="Due (₹)"
                    value={`${dueAmount.toLocaleString("en-IN")}`}
                    disabled
                  />
                </div>
                <div className="item2">
                  <FormDateInput
                    label="Payment Date"
                    max={localTodayStr()}
                    value={formData.payment_date}
                    onChange={(e) => setFormData({ ...formData, payment_date: e.target.value })}
                    required
                  />
                </div>
                <div className="item3">
                    <DropDown
                        label="Payment Mode"
                        selected={formData.payment_mode}
                        setSelected={handlePaymentModeChange}
                        options={PAYMENT_MODE_OPTIONS}
                        isRequired={true}
                        disabled={!!linkedPdc}
                        Height="auto"
                        minheight="150px"
                        OverFlow="auto"
                    />
                    </div>

                <div className="item4">
                  <FormInputs
                    label={referenceFieldLabel}
                    value={formData.transaction_ref}
                    maxLength={100}
                    onChange={(e) => setFormData({ ...formData, transaction_ref: e.target.value })}
                    disabled={isTransactionNoDisabled}
                    required={!isTransactionNoDisabled}
                  />
                </div>
                <div className="item5">
                    <DropDown
                        label="Bank Name"
                        selected={bankList.find((b) => b.bank_name === formData.bank_name)?.bank_id ?? null}
                        setSelected={(bankId) => {
                          const bank = bankList.find((b) => b.bank_id === bankId);
                          setFormData({ ...formData, bank_name: bank?.bank_name || "" });
                        }}
                        options={bankList.map((b) => ({ id: b.bank_id, label: b.bank_name }))}
                        placeholder="Select bank"
                        disabled={isBankNameDisabled}
                        Height="auto"
                        minheight="150px"
                        OverFlow="auto"
                    />
                </div>
                <div className="item6">
                  <FormInputs
                    label="Receipt Number"
                    value={formData.receipt_no}
                    placeholder="Enter receipt number"
                    maxLength={10}
                    onChange={(e) => setFormData({ ...formData, receipt_no: e.target.value })}
                    required
                  />
                </div>

                <div className="item1">
                  <FormInputs
                    label="Remarks"
                    value={formData.remarks}
                    placeholder="Write your remarks here..."
                    onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default PaymentRecord;